import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './BookingAgent.scss';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  flightOptionsInitialValues,
  flightOptionsValidationSchema,
  hotelOptionsInitialValues,
  hotelOptionsValidationSchema,
  renderStatusColors,
} from './constants';
import {
  BookingAgentGeneralInfo,
  ClientInformation,
  FlightInformation,
  FlightOption,
  HotelInformation,
  HotelOption,
  ViewTravellerInfoModal,
  UploadAttachment,
  BookingStatus,
  AirportTransfer,
  BookingComments,
  RejectBookingModal,
  VisaInformation,
  TravelInsurance,
  ViewRiskCountry,
  QuoteComments,
  ConfirmOverlimit,
} from '../../../components/BookingAgent';
import {
  cancelTravelBooking,
  confirmTravelBooking,
  fetchTravelOptions,
  modifyTravelBooking,
  saveTravelBooking,
} from '../../../store/apis/travelService';
import {
  buildBookingPayload,
  buildCancelBookingPayload,
  buildRebookingPayload,
  getTrimmedString,
  isConfirmBookingOverLimit,
  mapFlightOptionsToRequestBody,
  mapHotelOptionsToRequestBody,
  validateComponent,
  validateConfirmBooking,
} from './BookingAgentUtil';
import { Badge } from '../../../components/Common';
import { getCompanyCode } from '../../../config/axios.config';
import { getAuthUser } from '../../../utils/auth.util';

function BookingAgent() {
  document.title = 'Booking Agent | Envision - Daikin';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [headerDetails, setHeaderDetails] = useState({});
  const [status, setStatus] = useState('');
  const [rejectOption, setRejectOption] = useState('');
  const queryClient = useQueryClient();
  // Refs for various details
  const visaDetailsRef = useRef({});
  const travelInsuranceDetailsRef = useRef({});
  const airportTransferDetailsRef = useRef({});
  const bookingCommentsRef = useRef({});
  const bookingStatusRef = useRef({});
  const quoteCommentsRef = useRef({});
  // Form Refs
  const bookingStatusFormRef = useRef(null);
  const airportTransferFormRef = useRef(null);
  const travelInsuranceFormRef = useRef(null);
  const visaFormRef = useRef(null);
  const bookingCommentFormRef = useRef(null);
  const quoteCommentFormRef = useRef(null);
  const attachmentRef = useRef(null);

  const { companyList } = getAuthUser();

  // State for View Traveller Information Modal
  const [viewTravellerModal, setViewTravellerModal] = useState(false);
  const toggleViewTravellerModal = () => {
    setViewTravellerModal(!viewTravellerModal);
  };

  const [rejectBookingModal, setRejectBookingModal] = useState(false);
  const toggleRejectBookingModal = () => {
    setRejectBookingModal(!rejectBookingModal);
  };

  const [riskCountryModal, setRiskCountryModal] = useState(false);
  const toggleRiskCountryModal = () => {
    setRiskCountryModal(!riskCountryModal);
  };

  const [confirmOverlimitModal, setConfirmOverlimitModal] = useState(false);
  const toggleConfirmOverlimitModal = () => {
    setConfirmOverlimitModal(!confirmOverlimitModal);
  };

  const getLocalStorageCompany = () => {
    const companyNo = getCompanyCode();
    const localStorageCompany = companyList?.find(
      company => company.Code === companyNo,
    );
    return localStorageCompany;
  };

  // Get the request ID from URL params
  const id = searchParams.get('ID');
  const r = searchParams.get('R');

  // Refs for flight options
  const flightOptionRefs = [useRef(), useRef(), useRef()];
  const {
    control: flightBookingControl,
    handleSubmit: flightBookingHandleSubmit,
    getValues: flightBookingGetValues,
    setValue: flightBookingSetValue,
    watch: flightBookingWatch,
    formState: { errors: flightBookingErrors },
    trigger: flightBookingTriggerValidation,
  } = useForm({
    defaultValues: { options: flightOptionsInitialValues },
    resolver: yupResolver(flightOptionsValidationSchema),
    mode: 'onChange',
  });

  // Refs for hotel options
  const hotelOptionRefs = [useRef(), useRef(), useRef()];
  const {
    control: hotelBookingControl,
    handleSubmit: hotelBookingHandleSubmit,
    getValues: hotelBookingGetValues,
    trigger: hotelBookingTriggerValidation,
    setValue: hotelBookingSetValue,
    formState: { errors: hotelBookingErrors },
  } = useForm({
    defaultValues: { options: hotelOptionsInitialValues },
    resolver: yupResolver(hotelOptionsValidationSchema),
    mode: 'onChange',
  });

  // Get Booking Agent Data
  const { data: bookingAgentData } = useQuery({
    queryKey: ['bookingAgentData'],
    queryFn: () =>
      fetchTravelOptions({
        requestNo: id,
        revisionNo: r,
      }),
  });

  // Get selected flight and hotel options from header details
  const getSelectedOptionsFromHeader = () => {
    const selectedFlightNo = headerDetails?.Selected_Option_Flight;
    const selectedHotelNo = headerDetails?.Selected_Option_Hotel;

    const getFlightOption = num => {
      if (!num || num < 1 || num > 3) return null;

      return {
        travelAgency: headerDetails[`Option${num}_Flight_TravelAgency`],
        currency: headerDetails[`Option${num}_Flight_Curr`],
        travelAgencyDescription:
          headerDetails[`Option${num}_Flight_TravelAgency_Description`],
        quoteValue: headerDetails[`Option${num}_Flight_QuoteValue`],
        refund: headerDetails[`Option${num}_Flight_Refund`],
        rebooking: headerDetails[`Option${num}_Flight_Rebooking`],
        terms: headerDetails[`Option${num}_Flight_Terms`],
      };
    };

    const getHotelOption = num => {
      if (!num || num < 1 || num > 3) return null;

      return {
        currency: headerDetails[`Option${num}_Hotel_Curr`],
        ratesPerNight: headerDetails[`Option${num}_Hotel_RatesPerNight`],
        quoteValue: headerDetails[`Option${num}_Hotel_QuoteValue`],
        terms: headerDetails[`Option${num}_Hotel_Terms`],
        travelAgency: headerDetails[`Option${num}_Hotel_Travel_Agency`],
      };
    };

    return {
      selectedFlightOption: getFlightOption(selectedFlightNo),
      selectedHotelOption: getHotelOption(selectedHotelNo),
    };
  };

  // Get selected flight and hotel options from header details
  const { selectedFlightOption, selectedHotelOption } =
    getSelectedOptionsFromHeader(headerDetails);

  const flightBookingValues = flightBookingGetValues('options');
  const hotelBookingValues = hotelBookingGetValues('options');

  const validateAll = async () => {
    const checks = [];

    if (headerDetails?.Flight === 'Y' && status === 'Request for Quotation') {
      checks.push({
        name: 'Flight',
        promise: flightBookingTriggerValidation(),
      });
    }
    if (headerDetails?.Hotel === 'Y' && status === 'Request for Quotation') {
      checks.push({ name: 'Hotel', promise: hotelBookingTriggerValidation() });
    }
    if (bookingStatusFormRef.current) {
      checks.push({
        name: 'BookingStatus',
        promise: bookingStatusFormRef.current.triggerAll(),
      });
    }
    if (airportTransferFormRef.current) {
      checks.push({
        name: 'AirportTransfer',
        promise: airportTransferFormRef.current.triggerAll(),
      });
    }
    if (travelInsuranceFormRef.current) {
      checks.push({
        name: 'TravelInsurance',
        promise: travelInsuranceFormRef.current.triggerAll(),
      });
    }
    if (visaFormRef.current) {
      checks.push({ name: 'Visa', promise: visaFormRef.current.triggerAll() });
    }
    if (quoteCommentFormRef.current) {
      checks.push({
        name: 'QuoteComment',
        promise: quoteCommentFormRef.current.triggerAll(),
      });
    }
    if (bookingCommentFormRef.current) {
      checks.push({
        name: 'BookingComment',
        promise: bookingCommentFormRef.current.triggerAll(),
      });
    }

    // Resolve all
    const results = await Promise.all(checks.map(c => c.promise));

    // Attach results back to names
    const labeledResults = checks.map((c, i) => ({
      name: c.name,
      result: results[i],
    }));

    return labeledResults.every(res => res.result);
  };

  // Mutation to save booking
  const { mutate: saveBooking, isPending: saveBookingPending } = useMutation({
    mutationFn: async payload => saveTravelBooking(payload),
    onSuccess: response => {
      if (response?.flag === 'True') {
        toast.success(
          response?.message ?? `Request Booking ${id} saved successfully.`,
          {
            className: 'toast-success',
          },
        );
        navigate(`/hr/booking-agent?ID=${id}&R=${r}`);
        queryClient.invalidateQueries({ queryKey: ['bookingAgentData'] });
      } else if (response?.flag === 'False') {
        const errors = response?.errorData;
        if (response?.message) {
          toast.error(response?.message);
        }
        if (errors && errors.length > 0) {
          errors.forEach(error => {
            toast.error(error[0]['Error Message'] || 'An error occurred', {
              className: 'toast-error',
            });
          });
        }
      }
    },

    onError: () => {
      toast.error('Failed to save booking. Please try again.', {
        className: 'toast-error',
      });
    },
  });

  // Mutation to Confirm Booking -- Pending for Booking Status
  const confirmMutation = useMutation({
    mutationFn: async payload => {
      const res = await confirmTravelBooking(payload);
      if (res?.flag !== 'True') {
        const msg =
          res?.errorData?.flat?.()?.[0]?.['Error Message'] ||
          res?.message ||
          'Booking failed.';
        throw new Error(msg);
      }
      return res;
    },
    onSuccess: (response, variables) => {
      if (!variables?.navigateOnSuccess) return;
      toast.success(
        response?.message ?? `Request Booking ${id} confirmed successfully.`,
        { className: 'toast-success' },
      );
      navigate(`/hr/booking-agent?ID=${id}&R=${r}`);
      queryClient.invalidateQueries({ queryKey: ['bookingAgentData'] });
    },
    onError: error => {
      toast.error(
        error?.message || 'Booking failed due to a network or server error.',
        {
          className: 'toast-error',
        },
      );
    },
  });

  // Mutation to modify booking
  const { mutate: modifyBooking, isPending: modifyPending } = useMutation({
    mutationFn: async payload => modifyTravelBooking(payload),
    onSuccess: response => {
      if (response?.flag === 'True') {
        toast.success(
          response?.message ?? `Request Booking ${id} modified successfully.`,
          {
            className: 'toast-success',
          },
        );
        queryClient.invalidateQueries({ queryKey: ['bookingAgentData'] });
        navigate('/hr/booking-responsible');
      } else if (response?.flag === 'False') {
        const errors = response?.errorData;
        if (errors && errors.length > 0) {
          errors.forEach(error => {
            toast.error(error[0]['Error Message'] || 'An error occurred', {
              className: 'toast-error',
            });
          });
        }
      }
    },

    onError: () => {
      toast.error('Booking failed due to a network or server error.');
    },
  });

  // Mutation to cancel booking
  const { mutate: cancelBooking, isPending: cancelPending } = useMutation({
    mutationFn: async payload => cancelTravelBooking(payload),
    onSuccess: response => {
      if (response?.flag === 'True') {
        toast.success(
          response?.message ?? `Request Booking ${id} cancelled successfully.`,
          {
            className: 'toast-success',
          },
        );
        queryClient.invalidateQueries({ queryKey: ['bookingAgentData'] });
        navigate('/hr/booking-responsible');
      } else if (response?.flag === 'False') {
        const errors = response?.errorData;
        if (errors && errors.length > 0) {
          errors.forEach(error => {
            toast.error(error[0]['Error Message'] || 'An error occurred', {
              className: 'toast-error',
            });
          });
        }
      }
    },
  });

  // Function to handle "Save Booking" and "Request for Quotation"
  const handleSaveBooking = async requestMode => {
    const { header: flightHeader, flightOptionList } =
      mapFlightOptionsToRequestBody(flightBookingValues);
    const { header: hotelHeader, hotelOptionList } =
      mapHotelOptionsToRequestBody(hotelBookingValues);
    const quoteComments =
      quoteCommentsRef?.current?.quoteComments?.trim() || '';
    const visaDetails = visaDetailsRef?.current || {};
    const travelInsuranceDetails = travelInsuranceDetailsRef?.current || {};
    const airportTransferDetails = airportTransferDetailsRef?.current || {};
    const allValid = await validateAll();
    if (!allValid) {
      const el = document.querySelector('.is-invalid');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      toast.error('Please fix the highlighted fields before continuing.', {
        className: 'toast-error',
      });
      return;
    }

    const finalRequestPayload = {
      header: {
        requestNo: id,
        revisionNo: r,
        requestMode: String(requestMode),
        ...flightHeader,
        ...hotelHeader,
        visaCost: visaDetails?.visaCost || '',
        visaComments: visaDetails?.visaComments || '',
        travelInsuranceComments:
          travelInsuranceDetails?.travelInsuranceComments || '',
        travelInsuranceCost: travelInsuranceDetails?.travelInsuranceCost || 0,
        airportTransferComments:
          airportTransferDetails?.airportTransferComments || '',
        airportTransferCost: airportTransferDetails?.airportTransferCost || 0,

        quoteComments: quoteComments || '',
      },
      flightOptions: flightOptionList,
      hotelOptions: hotelOptionList,
    };

    // Validation errors
    if (requestMode !== 'S') {
      if (status === 'Request for Quotation') {
        if (!quoteComments || quoteComments === '') {
          toast.error('Quote booking comments are required.', {
            className: 'toast-error',
          });
          return;
        }

        // Validate flight form if flight is required
        if (headerDetails?.Flight === 'Y') {
          const flightValidationResult =
            await flightBookingTriggerValidation('options.0');
          if (!flightValidationResult) {
            toast.error(
              'At least one field in Flight Option 1 must be filled.',
              {
                className: 'toast-error',
              },
            );
            return;
          }
        }

        // Validate hotel form if hotel is required
        if (headerDetails?.Hotel === 'Y') {
          const hotelValidationResult =
            await hotelBookingTriggerValidation('options.0');
          if (!hotelValidationResult) {
            toast.error(
              'At least one field in Hotel Option 1 must be filled.',
              {
                className: 'toast-error',
              },
            );
            return;
          }
        }
      }
    }

    saveBooking(finalRequestPayload);
  };

  // Function to handle "Confirm Booking"
  const handleConfirmBooking = async requestMode => {
    const isSaveMode = requestMode === 'S';
    const bookingStatus = bookingStatusRef?.current || {};
    const approverStatus = headerDetails?.ApprStatus || '';

    const allValid = await validateAll();
    if (!allValid) {
      const el = document.querySelector('.is-invalid');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      toast.error('Please fix the highlighted fields before continuing.', {
        className: 'toast-error',
      });
      return;
    }

    const bookingComments = getTrimmedString(
      bookingCommentsRef?.current?.bookingComments,
    );
    if (!bookingComments) {
      toast.error('Please enter booking comments.', {
        className: 'toast-error',
      });
      return;
    }

    if (!isSaveMode) {
      const valid = validateConfirmBooking(headerDetails, bookingStatus);
      if (!valid) return;
    }

    const basePayload = buildBookingPayload({
      id,
      r,
      bookingStatus,
      bookingComments,
      status,
    });
    const finalRequestPayload = {
      ...basePayload,
      userAction: String(requestMode),
      navigateOnSuccess: true,
    };

    const overLimit = isConfirmBookingOverLimit(status, bookingStatus, {
      flightQuote: bookingStatus?.flightQuoteCost,
      hotelQuote: bookingStatus?.hotelQuoteCost,
      visaQuote: bookingStatus?.visaQuoteCost,
      insQuote: bookingStatus?.travelInsuranceQuoteCost,
      airQuote: bookingStatus?.airportTransferQuoteCost,
    });

    // Case A: Over-limit & NOT HR → Save draft, then show modal, NO navigate
    if (overLimit && approverStatus !== 'H' && requestMode === 'B') {
      const savePayload = {
        ...basePayload,
        overlimit: true,
        approvalStatus: headerDetails?.ApprStatus || '',
        userAction: 'B',
        navigateOnSuccess: false,
      };
      try {
        await confirmMutation.mutateAsync(savePayload);
        window.scrollTo(0, 0);
        toggleConfirmOverlimitModal();
      } catch (err) {
        //
      }
      return;
    }

    // Case B: Over-limit & HR approver → Confirm, then navigate
    if (overLimit && approverStatus === 'H' && requestMode === 'B') {
      try {
        confirmMutation.mutate(finalRequestPayload);
      } catch {
        // Catch
      }
      return;
    }

    // Case C: Not over-limit → Confirm, then navigate
    try {
      confirmMutation.mutate(finalRequestPayload);
    } catch {
      // Catch
    }
  };

  // Function to handle "Request for Rebooking"
  const handleRebook = async requestMode => {
    const bookingStatus = bookingStatusRef?.current || {};

    // Base Payload
    const basePayload = buildRebookingPayload({
      id,
      r,
      bookingStatus,
    });

    // Final Payload with Request Type
    const finalRequestPayload = {
      ...basePayload,
      userAction: String(requestMode),
    };

    const overLimit = isConfirmBookingOverLimit(status, bookingStatus, {
      flightQuote: bookingStatus?.flightQuoteCost,
      hotelQuote: bookingStatus?.hotelQuoteCost,
      visaQuote: bookingStatus?.visaQuoteCost,
      insQuote: bookingStatus?.travelInsuranceQuoteCost,
      airQuote: bookingStatus?.airportTransferQuoteCost,
    });

    // Validate First
    const allValid = await validateAll();
    if (!allValid) {
      const el = document.querySelector('.is-invalid');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      toast.error('Please fix the highlighted fields before continuing.', {
        className: 'toast-error',
      });
      return;
    }

    // Check Request Mode
    if (requestMode === 'C') {
      cancelBooking(finalRequestPayload);
    } else if (requestMode === 'M') {
      // Check for Overlimit
      if (overLimit && headerDetails?.ApprStatus !== 'H') {
        // Save Booking via Confirm Booking
        const savePayload = {
          ...buildBookingPayload({
            id,
            r,
            bookingStatus,
            bookingComments: null,
            status,
          }),
          overlimit: true,
          approvalStatus: headerDetails?.ApprStatus || '',
          userAction: 'B',
          navigateOnSuccess: false,
        };
        try {
          await confirmMutation.mutateAsync(savePayload);
          window.scrollTo(0, 0);
          toggleConfirmOverlimitModal();
        } catch (err) {
          // Catch
        }
      } else if (
        (overLimit && headerDetails?.ApprStatus === 'H') ||
        !overLimit
      ) {
        modifyBooking(finalRequestPayload);
      }
    }
  };

  // Function to handle "Request for Cancellation"
  const handleCancelBooking = async requestMode => {
    const bookingStatus = bookingStatusRef?.current || {};

    const allValid = await validateAll();
    if (!allValid) {
      const el = document.querySelector('.is-invalid');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      toast.error('Please fix the highlighted fields before continuing.', {
        className: 'toast-error',
      });
      return;
    }

    // Validation
    const validations = [];
    if (headerDetails?.Flight === 'Y') {
      validations.push(
        validateComponent({
          label: 'Flight',
          cost: bookingStatus.flightCancelRefund,
          quote: bookingStatus.flightQuoteCost,
          status: bookingStatus.flightStatus,
          agency: 'VALID',
          comments: bookingStatus.flightComments,
          opt: requestMode,
        }),
      );
    }
    if (headerDetails?.Hotel === 'Y') {
      validations.push(
        validateComponent({
          label: 'Hotel',
          cost: bookingStatus.hotelCancelRefund,
          quote: bookingStatus.hotelQuoteCost,
          status: bookingStatus.hotelStatus,
          agency: 'VALID',
          comments: bookingStatus.hotelComments,
          opt: requestMode,
        }),
      );
    }
    if (headerDetails?.Visa === 'Y') {
      validations.push(
        validateComponent({
          label: 'Visa',
          cost: bookingStatus.visaCancelRefund,
          quote: bookingStatus.visaQuoteCost,
          status: bookingStatus.visaStatus,
          agency: bookingStatus.visaTravelAgency,
          comments: bookingStatus.visaComments,
          opt: requestMode,
        }),
      );
    }
    if (headerDetails?.TravelInsurance === 'Y') {
      validations.push(
        validateComponent({
          label: 'Insurance',
          cost: bookingStatus.travelCancelInsuranceRefund,
          quote: bookingStatus.travelInsuranceQuoteCost,
          status: bookingStatus.travelInsuranceStatus,
          agency: bookingStatus.travelInsuranceTravelAgency,
          comments: bookingStatus.travelInsuranceComments,
          opt: requestMode,
        }),
      );
    }
    if (headerDetails?.AirportTransfer === 'Y') {
      validations.push(
        validateComponent({
          label: 'AirportTransfer',
          cost: bookingStatus.airportCancelTransferRefund,
          quote: bookingStatus.airportTransferQuoteCost,
          status: bookingStatus.airportTransferStatus,
          agency: bookingStatus.airportTransferTravelAgency,
          comments: bookingStatus.airportTransferComments,
          opt: requestMode,
        }),
      );
    }
    const failed = validations.find(v => !v.isValid);
    if (failed) {
      toast.error(failed.message, {
        className: 'toast-error',
      });
      return;
    }

    const finalRequestPayload = {
      ...buildCancelBookingPayload({ id, r, bookingStatus }),
      userAction: String(requestMode),
    };

    cancelBooking(finalRequestPayload);
  };

  // Effect to set header details and status from bookingAgentData
  useEffect(() => {
    if (bookingAgentData) {
      setHeaderDetails(bookingAgentData?.headerDetails || {});
      setStatus(bookingAgentData?.headerDetails?.Status || '');
    }
  }, [bookingAgentData]);

  // Function to handle rejection of booking
  const handleRejectBooking = () => {
    setRejectOption();
    toggleRejectBookingModal();
  };

  // Render Buttons based on Status
  const renderButtons = () => {
    switch (status) {
      case 'Request for Quotation':
      case 'Pending for Submission':
        return (
          <>
            <button
              onClick={() => handleSaveBooking('S')}
              type="button"
              className="custom-btn"
            >
              {saveBookingPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => handleSaveBooking('R')}
              type="button"
              className="custom-btn"
            >
              Submit for Review
            </button>
            <button
              onClick={toggleViewTravellerModal}
              type="button"
              className="custom-btn"
            >
              View Traveller Information
            </button>
            <button
              type="button"
              className="custom-danger-btn"
              onClick={() => handleRejectBooking()}
            >
              Reject
            </button>
            {bookingAgentData?.riskDetails &&
              bookingAgentData?.riskDetails.length > 0 && (
                <button
                  type="button"
                  className="custom-btn"
                  onClick={toggleRiskCountryModal}
                >
                  View Risk Country Information
                </button>
              )}
          </>
        );
      case 'Pending for Booking':
        return (
          <>
            <button
              onClick={() => handleConfirmBooking('S')}
              type="button"
              className="custom-btn"
            >
              Save
            </button>
            <button
              onClick={() => handleConfirmBooking('B')}
              type="button"
              className="custom-btn"
            >
              Confirm Booking
            </button>
            <button
              type="button"
              className="custom-danger-btn"
              onClick={() => handleRejectBooking()}
            >
              Reject
            </button>
            {bookingAgentData?.riskDetails &&
              bookingAgentData?.riskDetails.length > 0 && (
                <button
                  type="button"
                  className="custom-btn"
                  onClick={toggleRiskCountryModal}
                >
                  View Risk Country Information
                </button>
              )}
          </>
        );
      case 'Request for Rebooking':
        return (
          <>
            <button
              type="button"
              className="custom-btn"
              onClick={() => handleRebook('M')}
            >
              {modifyPending ? 'Modifying...' : 'Confirm Modify Booking'}
            </button>
            <button
              type="button"
              className="custom-danger-btn"
              onClick={() => handleRejectBooking()}
            >
              Reject
            </button>
            {bookingAgentData?.riskDetails &&
              bookingAgentData?.riskDetails.length > 0 && (
                <button
                  type="button"
                  className="custom-btn"
                  onClick={toggleRiskCountryModal}
                >
                  View Risk Country Information
                </button>
              )}
          </>
        );
      case 'Request for Cancellation':
        return (
          <>
            <button
              type="button"
              className="custom-btn"
              onClick={() => handleCancelBooking('C')}
            >
              {cancelPending ? 'Cancelling...' : 'Confirm Cancel Booking'}
            </button>
            <button
              onClick={() => handleRejectBooking()}
              type="button"
              className="custom-danger-btn"
            >
              Reject
            </button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-content bg-white">
      <div className="ba-header">
        <div className="d-flex flex-row align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link p-0 back-btn fs-5"
            onClick={() => navigate('/hr/booking-responsible')}
          >
            <Icon path={mdiChevronLeft} size={1.1} />
            <span className="d-flex flex-row gap-2 fs-5">Back</span>
          </button>

          <div className="trd-separator" />
          <div
            className="d-flex flex-row gap-2 fs-5"
            style={{ fontSize: '14px' }}
          >
            <span className="fw-semibold fs-5">Request ID</span>
            <span
              className="fw-semibold fs-5"
              style={{ color: '#065A86', fontSize: '14px' }}
            >
              {id}/{r}
            </span>
          </div>
          <div className="trd-separator" />
          <div
            className="d-flex flex-row align-items-center gap-2 fs-5"
            style={{ fontSize: '14px' }}
          >
            <span
              className="fw-semibold fs-5"
              style={{ color: '#065A86', fontSize: '14px' }}
            >
              Status
            </span>
          </div>
          <Badge config={renderStatusColors?.[status]} dot className="p-2">
            {status}
          </Badge>
        </div>
      </div>
      <div className="d-flex flex-column gap-2 py-2">
        {/* General Information */}
        <BookingAgentGeneralInfo data={headerDetails} />
        {/* Flight Information */}
        {bookingAgentData?.flightDetails &&
          bookingAgentData?.flightDetails.length > 0 && (
            <FlightInformation data={bookingAgentData?.flightDetails} />
          )}
        {/* Hotel Information -- Depends on Requirements */}
        {bookingAgentData?.hotelDetails &&
          bookingAgentData?.hotelDetails.length > 0 && (
            <HotelInformation data={bookingAgentData?.hotelDetails} />
          )}
        {/* Client Information -- Depends on Traveller Type */}
        {bookingAgentData?.clientDetails &&
          bookingAgentData?.clientDetails.length > 0 && (
            <ClientInformation data={bookingAgentData?.clientDetails} />
          )}
        {/* Flight Booking Options */}
        {headerDetails?.Flight === 'Y' && (
          <div className="d-flex flex-column gap-3 ba-form">
            {status === 'Request for Rebooking' ||
            status === 'Pending for Booking' ||
            status === 'Request for Cancellation' ? (
              <FlightOption
                index={(headerDetails?.Selected_Option_Flight || 1) - 1}
                control={flightBookingControl}
                handleSubmit={flightBookingHandleSubmit}
                getValues={flightBookingGetValues}
                trigger={flightBookingTriggerValidation}
                setValue={flightBookingSetValue}
                ref={
                  flightOptionRefs[
                    (headerDetails?.Selected_Option_Flight || 1) - 1
                  ]
                }
                headerDetails={headerDetails}
                flightOptions={
                  bookingAgentData?.flightOptions[
                    (headerDetails?.Selected_Option_Flight || 1) - 1
                  ]
                }
                status={status}
                watch={flightBookingWatch()}
                currentCompany={getLocalStorageCompany()}
              />
            ) : (
              [0, 1, 2].map(index => (
                <FlightOption
                  control={flightBookingControl}
                  handleSubmit={flightBookingHandleSubmit}
                  getValues={flightBookingGetValues}
                  trigger={flightBookingTriggerValidation}
                  setValue={flightBookingSetValue}
                  key={index}
                  index={index}
                  ref={flightOptionRefs[index]}
                  headerDetails={headerDetails}
                  flightOptions={bookingAgentData?.flightOptions[index]}
                  status={status}
                  flightBookingErrors={flightBookingErrors || {}}
                  currentCompany={getLocalStorageCompany()}
                />
              ))
            )}
          </div>
        )}
        {/* Hotel Booking Options */}
        {headerDetails?.Hotel === 'Y' && (
          <div className="d-flex flex-column ba-form gap-3">
            {status === 'Request for Rebooking' ||
            status === 'Pending for Booking' ||
            status === 'Request for Cancellation' ? (
              <HotelOption
                index={(headerDetails?.Selected_Option_Hotel || 1) - 1}
                control={hotelBookingControl}
                handleSubmit={hotelBookingHandleSubmit}
                getValues={hotelBookingGetValues}
                trigger={hotelBookingTriggerValidation}
                setValue={hotelBookingSetValue}
                ref={
                  hotelOptionRefs[
                    (headerDetails?.Selected_Option_Hotel || 1) - 1
                  ]
                }
                headerDetails={headerDetails}
                hotelOptions={
                  bookingAgentData?.hotelOptions[
                    (headerDetails?.Selected_Option_Hotel || 1) - 1
                  ]
                }
                status={status}
                currentCompany={getLocalStorageCompany()}
              />
            ) : (
              [0, 1, 2].map(index => (
                <HotelOption
                  key={index}
                  index={index}
                  control={hotelBookingControl}
                  handleSubmit={hotelBookingHandleSubmit}
                  getValues={hotelBookingGetValues}
                  trigger={hotelBookingTriggerValidation}
                  setValue={hotelBookingSetValue}
                  ref={hotelOptionRefs[index]}
                  headerDetails={headerDetails}
                  hotelOptions={bookingAgentData?.hotelOptions[index]}
                  status={status}
                  hotelBookingErrors={hotelBookingErrors || {}}
                  currentCompany={getLocalStorageCompany()}
                />
              ))
            )}
          </div>
        )}
        {/* Quote Comments */}
        {status === 'Request for Quotation' && (
          <QuoteComments
            ref={quoteCommentFormRef}
            data={headerDetails}
            status={status}
            onDataChange={updatedValue => {
              quoteCommentsRef.current = updatedValue;
            }}
          />
        )}
        {/* Booking Comments */}
        {status !== 'Request for Quotation' &&
          status !== 'Request for Rebooking' &&
          status !== 'Request for Cancellation' && (
            <BookingComments
              ref={bookingCommentFormRef}
              data={headerDetails}
              status={status}
              onDataChange={updatedValue => {
                bookingCommentsRef.current = updatedValue;
              }}
            />
          )}
        {/* Upload Attachment */}
        <UploadAttachment id={id} ref={attachmentRef} />
        {/* Booking Status */}
        {(status === 'Pending for Booking' ||
          status === 'Request for Rebooking' ||
          status === 'Request for Cancellation') && (
          <BookingStatus
            ref={bookingStatusFormRef}
            data={headerDetails}
            selectedHotelOption={selectedHotelOption}
            selectedFlightOption={selectedFlightOption}
            onDataChange={updatedValue => {
              bookingStatusRef.current = updatedValue;
            }}
            status={status}
          />
        )}
        {/* Airport Transfer */}
        {headerDetails?.AirportTransfer === 'Y' &&
          status === 'Request for Quotation' && (
            <AirportTransfer
              ref={airportTransferFormRef}
              data={headerDetails}
              onDataChange={updatedValue => {
                airportTransferDetailsRef.current = updatedValue;
              }}
            />
          )}
        {/* Travel Insurance */}
        {headerDetails?.TravelInsurance === 'Y' &&
          status === 'Request for Quotation' && (
            <TravelInsurance
              ref={travelInsuranceFormRef}
              data={headerDetails}
              onDataChange={updatedValue => {
                travelInsuranceDetailsRef.current = updatedValue;
              }}
            />
          )}
        {/* Visa */}
        {headerDetails?.Visa === 'Y' && status === 'Request for Quotation' && (
          <VisaInformation
            ref={visaFormRef}
            data={headerDetails}
            onDataChange={updatedValue => {
              visaDetailsRef.current = updatedValue;
            }}
          />
        )}
        <div className="d-flex flex-row align-items-center justify-content-center gap-2 py-2">
          {renderButtons()}
        </div>
      </div>
      <ViewTravellerInfoModal
        isOpen={viewTravellerModal}
        toggle={toggleViewTravellerModal}
        data={bookingAgentData?.travelerDetails}
      />
      <RejectBookingModal
        isOpen={rejectBookingModal}
        toggle={toggleRejectBookingModal}
        setRejectOption={setRejectOption}
        rejectOption={rejectOption}
        data={headerDetails}
        id={id}
        r={r}
      />
      <ViewRiskCountry
        isOpen={riskCountryModal}
        toggle={toggleRiskCountryModal}
        data={bookingAgentData?.riskDetails}
      />
      <ConfirmOverlimit
        isOpen={confirmOverlimitModal}
        toggle={toggleConfirmOverlimitModal}
        r={r}
        id={id}
      />
    </div>
  );
}

export default BookingAgent;
